import {
    EXECUTION_POLL_INTERVAL_MS,
    EXECUTION_POLL_MAX_RETRY_INTERVAL_MS,
    GROUP_ACTIVE_STATUS_PRIORITY,
} from '../constants/canvas';
import { getImageSequenceLabel, getNodeImageSequence } from './imageSequence';

export const getExecutionPollRetryDelay = (failureCount) => Math.min(
    EXECUTION_POLL_INTERVAL_MS * (2 ** Math.max(failureCount - 1, 0)),
    EXECUTION_POLL_MAX_RETRY_INTERVAL_MS,
);

export const getNodeImageOutputs = (node) => (
    (node?.data?.latestExecution?.outputs || []).filter((item) => item?.type === 'image' && item?.url)
);

export const normalizeExecutionOutputs = (outputs = []) => (
    Array.isArray(outputs) ? outputs.filter((item) => item?.url) : []
);

export const getPrimaryOutputId = (outputs = []) => (
    normalizeExecutionOutputs(outputs).find((item) => item?.primary && item?.url)?.id || ''
);

export const resolveExecutionSelectedOutputId = (outputs = [], fallbackSelectedOutputId = '') => {
    const normalizedOutputs = normalizeExecutionOutputs(outputs);
    if (!normalizedOutputs.length) return '';

    const primaryOutputId = getPrimaryOutputId(normalizedOutputs);
    if (primaryOutputId) return primaryOutputId;

    const hasFallbackOutput = normalizedOutputs.some((item) => (
        String(item?.id ?? '') === String(fallbackSelectedOutputId ?? '')
    ));
    if (hasFallbackOutput) return fallbackSelectedOutputId;

    return normalizedOutputs[0]?.id || '';
};

export const resolveNodeExecutionSelectedOutputId = (nodeExecution, fallbackSelectedOutputId = '') => {
    const nextStatus = String(nodeExecution?.status || '').toLowerCase();

    if (nextStatus === 'manual') {
        return '';
    }

    return resolveExecutionSelectedOutputId(nodeExecution?.outputs, fallbackSelectedOutputId);
};

export const getExecutionStatusPriority = (status) => (
    GROUP_ACTIVE_STATUS_PRIORITY[String(status || '').toLowerCase()] || 0
);

export const inferActiveGroupExecutionMeta = (groupNodeId, projectNodes = []) => {
    const normalizedGroupNodeId = String(groupNodeId || '');
    if (!normalizedGroupNodeId) return null;

    const groupNode = projectNodes.find((node) => String(node?.id || '') === normalizedGroupNodeId) || null;
    const groupRenderData = groupNode?.renderData?.data || {};
    const groupChildNodes = projectNodes.filter((node) => (
        String(node?.parentId || '') === normalizedGroupNodeId
    ));
    if (!groupChildNodes.length) return null;

    const groupFunctionNodeIds = groupChildNodes
        .filter((node) => node?.nodeType === 'function')
        .map((node) => String(node?.id || ''))
        .filter(Boolean);
    const groupChildNodeIds = groupChildNodes
        .map((node) => String(node?.id || ''))
        .filter(Boolean);

    if (!groupFunctionNodeIds.length) return null;

    const executionCandidates = groupChildNodes
        .map((node) => ({
            nodeId: String(node?.id || ''),
            executionId: String(
                node?.latestExecution?.executionId
                || node?.renderData?.data?.activeExecutionId
                || '',
            ),
            status: String(
                node?.latestExecution?.status
                || node?.renderData?.data?.executionStatus
                || '',
            ).toLowerCase(),
            completedAt: String(node?.latestExecution?.completedAt || ''),
        }))
        .filter((item) => item.executionId)
        .sort((a, b) => {
            const priorityDiff = getExecutionStatusPriority(b.status) - getExecutionStatusPriority(a.status);
            if (priorityDiff !== 0) return priorityDiff;
            return String(b.completedAt).localeCompare(String(a.completedAt));
        });

    const activeExecutionCandidate = executionCandidates.find((item) => (
        getExecutionStatusPriority(item.status) > 0
    ));

    const fallbackGroupExecutionId = String(groupRenderData?.activeExecutionId || '');
    const isGroupLocked = !!groupRenderData?.groupExecutionLocked;
    const resolvedExecutionId = activeExecutionCandidate?.executionId || (
        isGroupLocked ? fallbackGroupExecutionId : ''
    );

    if (!resolvedExecutionId) return null;

    return {
        groupNodeId: normalizedGroupNodeId,
        executionId: resolvedExecutionId,
        groupFunctionNodeIds,
        groupChildNodeIds,
    };
};

export const buildActiveGroupExecutionMetaMap = (projectNodes = []) => {
    const groupMetaMap = new Map();

    projectNodes
        .filter((node) => String(node?.nodeType || '') === 'group')
        .forEach((groupNode) => {
            const meta = inferActiveGroupExecutionMeta(groupNode?.id, projectNodes);
            if (meta?.executionId) {
                groupMetaMap.set(String(meta.groupNodeId), meta);
            }
        });

    return groupMetaMap;
};

export const markExecutionPrimaryOutput = (outputs = [], selectedOutputId = '') => {
    const normalizedOutputs = normalizeExecutionOutputs(outputs);
    if (!normalizedOutputs.length) return [];

    const normalizedSelectedOutputId = String(selectedOutputId ?? '');
    const hasSelectedOutput = normalizedOutputs.some((item) => (
        String(item?.id ?? '') === normalizedSelectedOutputId
    ));
    if (!hasSelectedOutput) return normalizedOutputs;

    return normalizedOutputs.map((item) => ({
        ...item,
        primary: String(item?.id ?? '') === normalizedSelectedOutputId,
    }));
};

export const getPreferredNodeImageUrl = (node) => {
    if (!node) return '';
    if (node.type === 'imageNode') return node.data?.src || '';

    const outputs = getNodeImageOutputs(node);
    const primaryOutput = outputs.find((item) => item?.primary && item?.url);
    if (primaryOutput?.url) return primaryOutput.url;

    const selectedOutput = outputs.find((item) => String(item?.id ?? '') === String(node.data?.selectedOutputId ?? ''));
    if (selectedOutput?.url) return selectedOutput.url;

    return outputs[0]?.url || '';
};

export const getNodeImageSequenceLabel = (node) => (
    node?.type === 'groupNode'
        ? ''
        : (
            node?.data?.imageSequenceLabel
            || getImageSequenceLabel(getNodeImageSequence(node))
            || (node?.type === 'imageNode' ? node?.data?.label || '' : '')
        )
);

export const getNodeDownloadImageItems = (node) => {
    if (!node) return [];

    if (node.type === 'imageNode') return [];

    const outputs = getNodeImageOutputs(node);
    return outputs.map((item, index) => ({
        url: item.url,
        nodeId: node.id,
        nodeLabel: node.data?.label || 'node',
        outputId: item.id || '',
        index,
    }));
};

export const getNodeSourceImageItems = (node) => {
    if (!node) return [];

    if (node.type === 'imageNode') return [];

    const inputParams = node.data?.inputParams || {};
    const uploadUrls = [
        ...(Array.isArray(inputParams.imageUrls) ? inputParams.imageUrls : []),
        inputParams.srcImage,
        inputParams.refImage,
        inputParams.commodityImage,
        inputParams.matchImage,
        inputParams.angle1,
        inputParams.angle2,
    ];

    return uploadUrls
        .filter((url) => typeof url === 'string' && url)
        .map((url, index) => ({
            url,
            nodeId: node.id,
            nodeLabel: node.data?.label || 'node',
            index,
            category: 'source',
        }));
};

export const getNodeResultImageItems = (node) => (
    getNodeDownloadImageItems(node).map((item) => ({
        ...item,
        category: 'result',
    }))
);